import { useEffect, useRef } from 'react';

/**
 * FS-889 — the Device List's account and opening lifecycle, in one place.
 *
 * Everything asynchronous the page does on a report's behalf — reading or
 * writing a report, the report list, the CSV walk, Select all matching, a
 * confirmation the operator is still looking at — is a job that belongs to
 * the account signed in and the opening (the application of one report, or
 * of the Default view) it started under. A job takes a lease when it starts
 * and asks `alive()` before it touches anything. The answer is no once:
 *  - the page unmounted (the page is keyed by account, so a token for another
 *    account unmounts it);
 *  - a new opening began (a report chosen, Revert, Default view, a report
 *    closed, Back or Forward landing on another report);
 *  - the lease was aborted by its owner (a newer report list supersedes it).
 *
 * What a new opening does to the previous one's leases depends on their kind:
 *  - `read`: aborted, so no answer arrives at all;
 *  - `write`: retired but not aborted — a commit cannot be unsent, and its
 *    answer is the only way to learn whether it happened;
 *  - `account`: untouched — the report list outlives openings.
 * Unmounting aborts every lease of every kind.
 *
 * React's StrictMode runs an effect's setup, cleanup and setup again in
 * development, so the mounted flag is set in setup (not when the ref is
 * created) and the cleanup retires every job of the previous setup: the
 * second setup starts its own and nothing from the first can land.
 */

export type LeaseKind = 'read' | 'write' | 'account';

export interface Lease {
  readonly userId: number;
  /** The opening generation the job started under. */
  readonly opening: number;
  /** Given to the request; aborted when the lease is. */
  readonly signal: AbortSignal;
  /** The page is mounted, the account and opening are still current, and nothing aborted the lease. */
  alive(): boolean;
  /** The page is still mounted for this account (a retired write records its commit through this). */
  mounted(): boolean;
  /** Retire the lease now: its request is aborted and `alive()` is false from here on. */
  abort(): void;
  /** The job is over; the lease is forgotten (nothing to abort any more). */
  release(): void;
}

export interface OpeningLifecycle {
  /** The current opening generation. */
  opening(): number;
  /** Start a new opening: reads are aborted and every lease of the old one is retired. */
  newOpening(): number;
  /** A lease on the current account and opening for one job. */
  lease(kind?: LeaseKind): Lease;
  /** True between the effect's setup and its cleanup. */
  mounted(): boolean;
}

export function useOpeningLifecycle(userId: number): OpeningLifecycle {
  const openingRef = useRef(0);
  const mountedRef = useRef(false);
  const leasesRef = useRef(new Map<AbortController, LeaseKind>());
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  useEffect(() => {
    mountedRef.current = true;
    const leases = leasesRef.current;
    return () => {
      mountedRef.current = false;
      openingRef.current++;
      leases.forEach((_kind, controller) => controller.abort());
      leases.clear();
    };
  }, []);

  // One object for the life of the component, so it can sit in a hook's
  // dependency list without re-running anything.
  const apiRef = useRef<OpeningLifecycle | null>(null);
  if (apiRef.current === null) {
    const lease = (kind: LeaseKind = 'read'): Lease => {
      const opening = openingRef.current;
      const controller = new AbortController();
      leasesRef.current.set(controller, kind);
      const current = () => kind === 'account' || opening === openingRef.current;
      return {
        userId: userIdRef.current,
        opening,
        signal: controller.signal,
        alive: () => mountedRef.current && current() && !controller.signal.aborted,
        mounted: () => mountedRef.current,
        abort: () => {
          leasesRef.current.delete(controller);
          controller.abort();
        },
        release: () => {
          leasesRef.current.delete(controller);
        },
      };
    };
    apiRef.current = {
      opening: () => openingRef.current,
      newOpening: () => {
        const next = ++openingRef.current;
        leasesRef.current.forEach((kind, controller) => {
          if (kind !== 'read') return;
          leasesRef.current.delete(controller);
          controller.abort();
        });
        return next;
      },
      lease,
      mounted: () => mountedRef.current,
    };
  }
  return apiRef.current;
}
